import type { Timestamp, TmuxSession, UserOption, UUID } from '@/index'
import { agentModeToString, CommandType } from '@/index'
import type { UserHostDetails } from '@/infra/sse/shared/payloads'
import type { ClaudeSession, Command } from '@/features/events/conversation/state/types'
import { ErrorRecoveryType, isNotInteractable } from '@/features/events/conversation/state/types'
import type { ApiResult } from '@/infra/api'
import { API_ENDPOINTS, httpPost } from '@/infra/api'
import type { EncryptableField, EncryptedField, EncryptionMetadata } from '@/infra/crypto/payloads'

export interface ConversationCommandBody {
  readonly claudeSessionId: UUID
  readonly tmuxSession: TmuxSession
  readonly refEventId: UUID
  readonly currentUserPromptId: UUID | null
  readonly newUserPromptId: UUID | null
  readonly userPrompt: EncryptableField | null
  readonly agentMode: string | null
  readonly userOption: UserOption | null
  readonly userAnswers: Record<string, unknown> | EncryptedField | null
  readonly encryption?: EncryptionMetadata
}

export interface SystemCommandBody {
  readonly claudeSessionId: UUID
  readonly tmuxSession: TmuxSession
  readonly refEventId: UUID
  readonly currentUserPromptId: UUID | null
  readonly recoveryType: ErrorRecoveryType
}

export interface SessionCommandBody {
  readonly claudeSessionId: UUID | null
  readonly workDirectory: EncryptableField | null
  readonly sessionName: EncryptableField | null
  readonly initialMessage: EncryptableField | null
  readonly dangerouslySkipPermissions: boolean | null
  readonly encryption?: EncryptionMetadata
}

export interface CommandSubmissionPayload {
  readonly commandType: CommandType
  readonly timestamp: Timestamp
  readonly userHostDetails: UserHostDetails
  readonly conversationCommand: ConversationCommandBody | null
  readonly systemCommand: SystemCommandBody | null
  readonly sessionCommand: SessionCommandBody | null
}

export const commandClient = {
  submit(payload: CommandSubmissionPayload): Promise<ApiResult<void>> {
    return httpPost<void>(API_ENDPOINTS.COMMAND_SUBMIT, payload)
  },
}

export function buildSessionResumePayload(
  session: ClaudeSession,
  initialMessage: string | null,
): CommandSubmissionPayload {
  return {
    commandType: CommandType.RESUME_SESSION,
    timestamp: new Date().toISOString(),
    userHostDetails: session.userHostDetails,
    conversationCommand: null,
    systemCommand: null,
    sessionCommand: {
      claudeSessionId: session.sessionId,
      workDirectory: session.contextWorkDirectory,
      sessionName: null,
      initialMessage,
      dangerouslySkipPermissions: null,
    },
  }
}

export function buildSessionStartPayload(
  workDirectory: string,
  sessionName: string,
  initialMessage: string | null,
  dangerouslySkipPermissions: boolean,
  userHostDetails: UserHostDetails,
): CommandSubmissionPayload {
  return {
    commandType: CommandType.START_SESSION,
    timestamp: new Date().toISOString(),
    userHostDetails,
    conversationCommand: null,
    systemCommand: null,
    sessionCommand: {
      claudeSessionId: null,
      workDirectory,
      sessionName,
      initialMessage,
      dangerouslySkipPermissions,
    },
  }
}

function buildSessionLifecyclePayload(
  commandType: CommandType.END_SESSION | CommandType.ARCHIVE_SESSION,
  session: ClaudeSession,
): CommandSubmissionPayload {
  return {
    commandType,
    timestamp: new Date().toISOString(),
    userHostDetails: session.userHostDetails,
    conversationCommand: null,
    systemCommand: null,
    sessionCommand: {
      claudeSessionId: session.sessionId,
      workDirectory: null,
      sessionName: null,
      initialMessage: null,
      dangerouslySkipPermissions: null,
    },
  }
}

export function buildSessionTerminatePayload(session: ClaudeSession): CommandSubmissionPayload {
  return buildSessionLifecyclePayload(CommandType.END_SESSION, session)
}

export function buildSessionArchivePayload(session: ClaudeSession): CommandSubmissionPayload {
  return buildSessionLifecyclePayload(CommandType.ARCHIVE_SESSION, session)
}

export function buildCommandPayload(
  command: Command,
  session: ClaudeSession,
  currentUserPromptId: UUID | null,
  newUserPromptId: UUID | null,
): CommandSubmissionPayload {
  if (isNotInteractable(session) || !session.tmuxSession) {
    throw new Error('Cannot submit user command for non-interactable claude code sessions')
  }

  const tmuxSession = session.tmuxSession

  const sessionRouting = {
    claudeSessionId: session.sessionId,
    tmuxSession,
    refEventId: command.refEventId,
  }

  if (command.commandType === CommandType.ERROR_RECOVERY) {
    return {
      commandType: command.commandType,
      timestamp: command.timestamp,
      userHostDetails: session.userHostDetails,
      conversationCommand: null,
      systemCommand: {
        ...sessionRouting,
        currentUserPromptId,
        recoveryType: command.errorRecovery ?? ErrorRecoveryType.API_ERROR_RECOVERY,
      },
      sessionCommand: null,
    }
  }

  const hasCurrentId = currentUserPromptId !== null
  const hasNewId = newUserPromptId !== null

  if (hasCurrentId === hasNewId) {
    throw new Error(
      'Exactly one of currentUserPromptId or newUserPromptId must be non-null. ' +
        `Got currentUserPromptId: ${currentUserPromptId}, newUserPromptId: ${newUserPromptId}`,
    )
  }

  return {
    commandType: command.commandType,
    timestamp: command.timestamp,
    userHostDetails: session.userHostDetails,
    conversationCommand: {
      ...sessionRouting,
      currentUserPromptId,
      newUserPromptId,
      userPrompt: command.userPrompt,
      agentMode: command.agentMode === null ? null : agentModeToString(command.agentMode),
      userOption: command.userOption,
      userAnswers: command.userAnswers,
    },
    systemCommand: null,
    sessionCommand: null,
  }
}
